/**
 * Implements an instance of Router5
 *
 * See: http://router5.github.io/
 */

import Marionette from "backbone.marionette";
import Radio from "backbone.radio";

import createRouter, { loggerPlugin } from "router5";
import browserPlugin from "router5/plugins/browser";
import RouteEmitter from "./middleware/emitter";
import previousRoutePlugin from "./plugins/previous-route";
import analyticsPlugin from "./plugins/analytics";

import RouteModel from "../models/route";
import MenuItemCollection from "../collections/menu-items";

const globalChannel = Radio.channel("global");
const reportChannel = Radio.channel("report");

/**
 * Configure browserPlugin
 *
 * https://github.com/router5/router5/blob/master/modules/plugins/browser
 */
const browserPluginOptions = {
  useHash: true,
};

const DEFAULTS = {
  // http://router5.github.io/docs/configuring-routes.html
  initialRoutes: [],
  // http://router5.github.io/docs/router-options.html
  routerOptions: {},
};

class Router extends Marionette.Object {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);

    // add hashchange handler for IE browsers > v9
    if (RVC.internetExplorerVersion && window.addEventListener) {
      // console.info("Detected IE%i", RVC.internetExplorerVersion);
      window.addEventListener(
        "hashchange",
        this._handleIEHashChange.bind(this),
        false
      );
    }
  }

  initialize() {
    // http://router5.github.io/docs/injectables.html
    const routerDependencies = {
      store: new MenuItemCollection(),
    };

    // instatiate router and configure plugins
    this.router = createRouter(
      this.options.initialRoutes,
      this.options.routerOptions,
      routerDependencies
    )
      .useMiddleware(RouteEmitter)
      .usePlugin(browserPlugin(browserPluginOptions))
      .usePlugin(previousRoutePlugin)
      .usePlugin(analyticsPlugin);

    if (this.options.useLogger) {
      this.router.usePlugin(loggerPlugin);
    }

    // start it
    this.router.start(this._onStart.bind(this));
  }

  /**
   * Callback for router's start method
   *
   * @param {Object} err
   */
  _onStart(err) {
    if (err) throw err;

    const dependencies = this.router.getDependencies();

    this.store = dependencies.store;

    // listen for additions to collection
    this.listenTo(this.store, "add", this._onRouteAdded);

    // setup request handlers
    globalChannel.reply("router:add", this._addRoutes, this);
    globalChannel.reply("router:defaultRoute", this._loadDefaultRoute, this);
    globalChannel.reply("router:previousRoute", this._loadPreviousRoute, this);
    globalChannel.reply("router:returnToRoute", this._onReturnToRoute, this);
    globalChannel.reply("router:getOptions", this._getOptions, this);
    globalChannel.reply("router:getState", this._getState, this);
    globalChannel.reply("router:matchPath", this._matchPath, this);
    globalChannel.reply("router:navigate", this._navigateToRoute, this);

    // notify interested parties
    globalChannel.trigger("router:ready");
  }

  /**
   * Wraps router.navigateToDefault()
   */
  _loadDefaultRoute(options = { reload: false }) {
    this.router.navigateToDefault(options);
  }

  /**
   * Loads previous route along with its state when available
   */
  _loadPreviousRoute(options = { reload: false }) {
    if (this.router.previousRoute) {
      this._navigateToRoute(this.router.previousRoute, {}, options);
    }
  }

  /**
   * This method behaves like router:navigate, but injects the most recent
   * params from history, when found, instead of the original params.
   *
   * @param {String} route
   */
  _onReturnToRoute(route, options = { reload: false }) {
    const found = reportChannel.request("history:find", route);
    if (found) {
      const params = found.getUrl().getData() || {};
      // return to route with current params
      this._navigateToRoute(route, params, options);
    } else {
      // return to route with default params
      this._navigateToRoute(route, {}, options);
    }
  }

  _handleIEHashChange() {
    // console.info("hashchange", location.hash);

    // extract path from URL
    const path = location.hash.split("#")[1];

    // see if a route matches that path
    const route = this._matchPath(path);

    // if so, navigate to it, otherwise navigate to the default route
    if (route) {
      this._navigateToRoute(route.name, route.params);
    } else {
      this._loadDefaultRoute();
    }
  }

  _getOptions() {
    return this.router.getOptions();
  }

  _getState() {
    const s = this.router.getState();
    return new RouteModel({
      route: s.name,
      nav: s.data,
      title: s.title,
      subtitle: s.subtitle,
    });
  }

  /**
   * Callback for requests to router:add
   *
   * Allows modules to register their routes. Loops through all routes to
   * validate the specified role, if any. Then registers valid routes.
   *
   * @param {Brace.Collection} routes
   */
  _addRoutes(routes) {
    // process children to validate roles, if any
    routes.map((route) => {
      // create empty collection for children
      const _children = new MenuItemCollection();

      // does this route have children?
      if (route.has("children")) {
        // add the children data to collection, creates models and
        // validates role automatically
        _children.add(route.getChildren());

        // remove routes with an invalid role
        _children.remove(_children.where({ validRole: false }));

        // find first default route, if any
        const defaultRoute = _children.findWhere({ defaultRoute: true });
        if (defaultRoute) {
          // make sure only the default route is marked true
          _children.each((child) => {
            const flag = child.id === defaultRoute.id ? true : false;
            child.setDefaultRoute(flag);
          });

          // set the parent route on
          route.setDefaultRoute(true);
        }

        // update original children
        route.setChildren(_children.toJSON());
      }

      // does top route have a valid role?
      if (route.getValidRole()) {
        // add top route's JSON to collection
        this._addToStore(route.toJSON());
      }
    });
  }

  _addToStore(route) {
    // are we allowed to change existing routes?
    if (this.options.isMutable) {
      // does the route exist?
      if (this.store.get(route.name)) {
        // delete it
        this.store.remove(route.name);
      }
    }

    // route to store
    this.store.add(route);
  }

  /**
   * Attempts to route, w/reload, to a route triggered by name.
   *
   * @param {String} routeName
   * @param {Object} routeOptions
   * @param {Object} navOptions
   */
  _navigateToRoute(routeName, routeOptions = {}, navOptions = {}) {
    // console.log("_navigateToRoute", { routeName, routeOptions, navOptions });
    this.router.navigate(
      routeName,
      routeOptions,
      navOptions,
      this._onRouteNavigated.bind(this)
    );
  }

  /**
   * Callback for _navigateToRoute().
   *
   * @param {Object} err
   */
  _onRouteNavigated(err) {
    if (err) {
      // failure to route; load default route
      this._loadDefaultRoute();
    }
  }

  /**
   * Returns the state for a matched path
   *
   * @param {String} path
   */
  _matchPath(path) {
    return this.router.matchPath(path);
  }

  /**
   * Callback for collection adds. Inserts individual routes into router
   *
   * @param {Backbone.Model} model
   */
  _onRouteAdded(model) {
    // add it to router
    try {
      this.router.add(model.toJSON());
    } catch (err) {
      // console.warn("Unable to add route: %s.", model.get("name"), err);
    }
  }
}

export { Router as default };
